import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Cpu, Loader2, Filter, Plus, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Provider = 'OpenAI' | 'Anthropic' | 'Google' | 'Azure' | 'Custom';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type ModelStatus = 'active' | 'under_review' | 'deprecated';
type Capability = 'text-generation' | 'embeddings' | 'image-generation' | 'code' | 'vision';

interface AIModel {
  id: string;
  name: string;
  provider: Provider;
  version: string;
  capabilities: Capability[];
  risk_level: RiskLevel;
  status: ModelStatus;
  created_at: string;
}

interface ModelsResponse {
  models: AIModel[];
  total: number;
  active: number;
  under_review: number;
  deprecated: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROVIDERS: Provider[] = ['OpenAI', 'Anthropic', 'Google', 'Azure', 'Custom'];
const CAPABILITIES: Capability[] = ['text-generation', 'embeddings', 'image-generation', 'code', 'vision'];
const RISK_LEVELS: RiskLevel[] = ['low', 'medium', 'high', 'critical'];
const STATUSES: { value: ModelStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'deprecated', label: 'Deprecated' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadge(status: ModelStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'active':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Active</span>;
    case 'under_review':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Under Review</span>;
    case 'deprecated':
      return <span className={`${base} bg-muted text-muted-foreground`}>Deprecated</span>;
  }
}

function riskBadge(level: RiskLevel) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (level) {
    case 'critical':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Critical</span>;
    case 'high':
      return <span className={`${base} bg-orange-500/15 text-orange-500`}>High</span>;
    case 'medium':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Medium</span>;
    case 'low':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Low</span>;
  }
}

function providerBadge(provider: Provider) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (provider) {
    case 'OpenAI':
      return <span className={`${base} bg-emerald-500/15 text-emerald-500`}>OpenAI</span>;
    case 'Anthropic':
      return <span className={`${base} bg-orange-500/15 text-orange-500`}>Anthropic</span>;
    case 'Google':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Google</span>;
    case 'Azure':
      return <span className={`${base} bg-cyan-500/15 text-cyan-500`}>Azure</span>;
    case 'Custom':
      return <span className={`${base} bg-purple-500/15 text-purple-500`}>Custom</span>;
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ModelsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [filterProvider, setFilterProvider] = useState<string>('all');

  // Form state
  const [name, setName] = useState('');
  const [provider, setProvider] = useState<Provider>('OpenAI');
  const [version, setVersion] = useState('');
  const [capabilities, setCapabilities] = useState<Set<Capability>>(new Set());
  const [riskLevel, setRiskLevel] = useState<RiskLevel>('medium');
  const [status, setStatus] = useState<ModelStatus>('under_review');

  const modelsQuery = useQuery<ModelsResponse>({
    queryKey: ['governance', 'models'],
    queryFn: () => api<ModelsResponse>('governance/models'),
  });

  const registerMutation = useMutation({
    mutationFn: (body: Omit<AIModel, 'id' | 'created_at'>) =>
      api<AIModel>('governance/models', { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'models'] });
      resetForm();
    },
  });

  function resetForm() {
    setShowForm(false);
    setName('');
    setProvider('OpenAI');
    setVersion('');
    setCapabilities(new Set());
    setRiskLevel('medium');
    setStatus('under_review');
  }

  function toggleCapability(cap: Capability) {
    setCapabilities((prev) => {
      const next = new Set(prev);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !version.trim() || capabilities.size === 0) return;
    registerMutation.mutate({
      name: name.trim(),
      provider,
      version: version.trim(),
      capabilities: [...capabilities],
      risk_level: riskLevel,
      status,
    });
  }

  const models = modelsQuery.data?.models ?? [];
  const filtered = filterProvider === 'all' ? models : models.filter((m) => m.provider === filterProvider);
  const stats = modelsQuery.data;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Cpu className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">AI Model Registry</h1>
            <p className="text-muted-foreground mt-1">Register, track, and manage all AI models across your organization.</p>
          </div>
        </div>
        {!showForm && (
          <button type="button" onClick={() => setShowForm(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0">
            <Plus className="h-4 w-4" />
            Register Model
          </button>
        )}
      </div>

      {/* Stats */}
      {modelsQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Total Models</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-500">{stats?.active ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Under Review</p>
            <p className="text-2xl font-bold text-yellow-500">{stats?.under_review ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Deprecated</p>
            <p className="text-2xl font-bold text-muted-foreground">{stats?.deprecated ?? 0}</p>
          </div>
        </div>
      )}

      {/* Register form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Register New Model</h3>
            <button type="button" onClick={resetForm} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Model Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. GPT-4o" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Provider</label>
              <select value={provider} onChange={(e) => setProvider(e.target.value as Provider)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Version</label>
              <input value={version} onChange={(e) => setVersion(e.target.value)} required placeholder="e.g. 2024-01-25" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Risk Level</label>
              <select value={riskLevel} onChange={(e) => setRiskLevel(e.target.value as RiskLevel)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                {RISK_LEVELS.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as ModelStatus)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Capabilities</label>
            <div className="flex flex-wrap gap-3">
              {CAPABILITIES.map((cap) => (
                <label key={cap} className="flex items-center gap-2 cursor-pointer select-none text-sm text-foreground">
                  <input type="checkbox" checked={capabilities.has(cap)} onChange={() => toggleCapability(cap)} className="h-4 w-4 rounded border-border text-primary focus:ring-primary" />
                  {cap}
                </label>
              ))}
            </div>
          </div>

          {registerMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
              {registerMutation.error instanceof Error ? registerMutation.error.message : 'Failed to register model.'}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button type="submit" disabled={registerMutation.isPending || !name.trim() || !version.trim() || capabilities.size === 0} className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {registerMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {registerMutation.isPending ? 'Registering...' : 'Register Model'}
            </button>
            <button type="button" onClick={resetForm} className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <select value={filterProvider} onChange={(e) => setFilterProvider(e.target.value)} className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
          <option value="all">All Providers</option>
          {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Model cards grid */}
      {modelsQuery.isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-48 rounded-lg bg-muted animate-pulse" />)}
        </div>
      )}

      {modelsQuery.isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-center">
          <p className="text-sm text-red-500">
            Failed to load models.{' '}
            <button type="button" onClick={() => modelsQuery.refetch()} className="underline hover:no-underline">Retry</button>
          </p>
        </div>
      )}

      {modelsQuery.isSuccess && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
          {filterProvider === 'all' ? 'No models registered yet. Click "Register Model" to get started.' : `No models found for provider "${filterProvider}".`}
        </div>
      )}

      {modelsQuery.isSuccess && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((model) => (
            <div key={model.id} className="rounded-lg border border-border bg-card p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-medium text-foreground">{model.name}</h3>
                {statusBadge(model.status)}
              </div>
              <div className="flex items-center gap-2">
                {providerBadge(model.provider)}
                <span className="text-xs text-muted-foreground">v{model.version}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Risk:</span>
                {riskBadge(model.risk_level)}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {model.capabilities.map((cap) => (
                  <span key={cap} className="px-2 py-0.5 rounded-full text-xs bg-accent text-accent-foreground">{cap}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
